import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  Loader2,
  CheckCircle2,
  Handshake,
  ShieldCheck,
  Coins,
  Calendar,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "Sriman Shield",
  "Sriman D",
];

const initial = {
  full_name: "",
  email: "",
  phone: "",
  company: "",
  vendor_type: "individual",
  country: "",
  city: "",
  website: "",
  services: [],
  pitch: "",
};

const inputClass =
  "w-full bg-[#0B132B]/60 border border-white/10 focus:border-red-500/60 focus:outline-none text-white placeholder-slate-500 px-4 py-3.5 text-sm rounded-sm transition-colors";

export default function VendorApply() {
  const [v, setV] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));
  const toggleService = (svc) =>
    setV((s) => ({
      ...s,
      services: s.services.includes(svc)
        ? s.services.filter((x) => x !== svc)
        : [...s.services, svc],
    }));

  const canSubmit = useMemo(
    () => v.full_name.trim() && v.email.trim() && v.services.length > 0,
    [v],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Please provide name, email and at least one service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/vendor-applications`, v);
      setSubmitted(true);
      toast.success("Application received.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Unable to submit — please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <main
        data-testid="vendor-apply-success"
        className="bg-[#040812] min-h-screen pt-32 pb-24 px-6"
      >
        <div className="max-w-2xl mx-auto bg-[#0B132B]/50 border border-white/10 p-8 md:p-12">
          <CheckCircle2 size={28} className="text-red-500" strokeWidth={1.5} />
          <span className="overline mt-8 block">Application received</span>
          <h1 className="mt-4 font-display text-white text-3xl md:text-4xl tracking-tighter font-light leading-tight">
            Thank you — we&apos;ll be in touch.
          </h1>
          <p className="mt-4 text-slate-400 text-sm md:text-base leading-relaxed">
            Our team will review your application. If approved, you will receive
            an email with your CRM login and a signed copy of the Master Service
            Agreement (MSA).
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-medium px-6 py-3.5 rounded-sm transition-colors"
            >
              Back to home
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white text-sm px-6 py-3.5 rounded-sm transition-colors"
            >
              Browse services
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="vendor-apply-page" className="bg-[#040812]">
      {/* Header */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-20 border-b border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-red-500/10 blur-3xl rounded-full pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <span className="overline">Partner with Sriman Solutions</span>
            <h1 className="mt-6 font-display text-white text-5xl md:text-6xl lg:text-7xl tracking-tighter font-light leading-[1.02]">
              Refer, close,{" "}
              <span className="text-red-500">get paid on the 5th</span>.
            </h1>
            <p className="mt-6 text-slate-400 text-base md:text-lg max-w-2xl leading-relaxed">
              We are opening a small cohort of independent partners to help take
              our enterprise services to more institutions. If approved, you
              will receive a Master Service Agreement and CRM access to manage
              your leads.
            </p>
          </div>
          <div className="lg:col-span-5 grid grid-cols-1 gap-3">
            <div className="bg-[#0B132B]/50 border border-white/10 p-5">
              <Coins size={16} className="text-red-500" />
              <div className="mt-3 font-display text-white text-lg">
                ₹20,000 + GST per successful sale
              </div>
              <div className="mt-1 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase">
                Flat commission
              </div>
            </div>
            <div className="bg-[#0B132B]/50 border border-white/10 p-5">
              <Calendar size={16} className="text-red-500" />
              <div className="mt-3 font-display text-white text-lg">
                Paid by the 5th of every month
              </div>
              <div className="mt-1 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase">
                Monthly payouts
              </div>
            </div>
            <div className="bg-[#0B132B]/50 border border-white/10 p-5">
              <ShieldCheck size={16} className="text-red-500" />
              <div className="mt-3 font-display text-white text-lg">
                Formal MSA on approval
              </div>
              <div className="mt-1 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase">
                Auto-generated, downloadable
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <form
            onSubmit={submit}
            data-testid="vendor-apply-form"
            className="bg-[#0B132B]/40 border border-white/10 p-8 md:p-10"
          >
            <div className="flex items-center gap-3">
              <Handshake size={20} className="text-red-500" strokeWidth={1.5} />
              <span className="overline">Partner application</span>
            </div>
            <h2 className="mt-4 font-display text-white text-3xl tracking-tight">
              Tell us about yourself
            </h2>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline mb-2 block">Full name *</label>
                <input
                  data-testid="apply-name"
                  value={v.full_name}
                  onChange={set("full_name")}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className="overline mb-2 block">Work email *</label>
                <input
                  data-testid="apply-email"
                  type="email"
                  value={v.email}
                  onChange={set("email")}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className="overline mb-2 block">Phone</label>
                <input value={v.phone} onChange={set("phone")} className={inputClass} />
              </div>
              <div>
                <label className="overline mb-2 block">Business name</label>
                <input
                  value={v.company}
                  onChange={set("company")}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="overline mb-2 block">Vendor type</label>
                <select
                  value={v.vendor_type}
                  onChange={set("vendor_type")}
                  className={inputClass}
                >
                  <option value="individual">Individual</option>
                  <option value="firm">Firm / LLP</option>
                  <option value="company">Company</option>
                </select>
              </div>
              <div>
                <label className="overline mb-2 block">Country</label>
                <input value={v.country} onChange={set("country")} className={inputClass} />
              </div>
              <div>
                <label className="overline mb-2 block">City</label>
                <input value={v.city} onChange={set("city")} className={inputClass} />
              </div>
              <div>
                <label className="overline mb-2 block">Website / LinkedIn</label>
                <input
                  value={v.website}
                  onChange={set("website")}
                  placeholder="https://…"
                  className={inputClass}
                />
              </div>
            </div>

            <div className="mt-8">
              <label className="overline mb-3 block">
                Products you want to sell * (pick one or more)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {SERVICES.map((svc) => {
                  const active = v.services.includes(svc);
                  return (
                    <button
                      key={svc}
                      type="button"
                      onClick={() => toggleService(svc)}
                      data-testid={`apply-service-${svc.replace(/[^A-Za-z0-9]/g, "_")}`}
                      className={`text-left px-4 py-3 rounded-sm border text-sm transition-colors ${
                        active
                          ? "border-red-500 bg-red-500/10 text-white"
                          : "border-white/10 text-slate-300 hover:border-white/25"
                      }`}
                    >
                      {svc}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-8">
              <label className="overline mb-2 block">Your pitch (optional)</label>
              <textarea
                rows={5}
                value={v.pitch}
                onChange={set("pitch")}
                placeholder="Relevant experience, existing network, why you want to partner with us…"
                className={inputClass}
              />
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="text-xs text-slate-500 font-mono tracking-wider">
                Submissions are treated confidentially. Approval typically
                within 3 business days.
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="apply-submit"
                className="inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-70 text-white font-medium px-7 py-3.5 rounded-sm transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Submitting…
                  </>
                ) : (
                  <>
                    Submit application
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}
